import { trackEvent as trackAdjustEvent } from './adjust';
import { trackGa4Event } from './ga4';
import { trackMoEngageEvent } from './moengage';

type AnalyticsEventParams = Record<string, unknown>;
type Fbq = (command: string, eventName: string, params?: AnalyticsEventParams) => void;

// Define supported event types
export type AnalyticsEventType = 'PAYMENT_INITIATED' | 'DOWNLOAD_INITIATED' | 'WEB_SUBSCRIBE_BUTTON_CLICKED' | 'PAYMENT_ERROR' | 'LOGIN_PAGE_VISITED' | 'PAYMENT_CANCELLED' | 'USER_LEFT_BEFORE_SUBSCRIBING' | 'WEB_SUBSCRIBE_PAGE_VISITED' | 'WEB_DOWNLOAD_PAGE_VISITED' | 'WEB_BOTTOM_SHEET_VIEWED' | 'WEB_PAYMENT_METHOD_SELECTED' | 'WEB_BOTTOM_SHEET_BTN_CLICKED' | 'WEB_POLLING_CANCELLED' | 'WEB_PAYMENT_SUCCESS_SCREEN_VIEWED' | 'WEB_PAYMENT_FAILURE_SCREEN_VIEWED' | 'WEB_SEND_OTP_BTN_CLICKED' | 'WEB_VERIFY_OTP_BTN_CLICKED' | 'WEB_SOCIAL_LOGIN_BTN_CLICKED' | 'WEB_RESEND_OTP_BTN_CLICKED' | 'WEB_LOGIN_SUCCESS' | 'WEB_LOGIN_FAILED' | 'REFERRAL_CODE_BTN_CLICKED' | 'APPLY_CODE_BTN_CLICKED' | 'CLAIM_CODE_BTN_CLICKED' | 'WEB_PAYMENT_SCREEN_MEDIA' | 'WEB_PAYMENT_SCREEN_VIDEO_PAUSE' | 'WEB_PAYMENT_SCREEN_VIDEO_PLAY' | 'WEB_PAYMENT_SCREEN_VIDEO_MUTED' | 'WEB_PAYMENT_SCREEN_VIDEO_UNMUTED' | 'CONTENT_IMPRESSION_WEB' | 'CONTENT_CLICKED_WEB' | 'SCREEN_VIEWED_WEB' | 'FREE_TRIAL_NUDGE_VIEWED_WEB' | 'FREE_TRIAL_NUDGE_CTA_CLICKED_WEB' | 'SEARCH_BUTTON_CLICKED_WEB' | 'PROFILE_BUTTON_CLICKED_WEB' | 'MYLIST_BUTTON_CLICKED_WEB' | 'HOME_BUTTON_CLICKED_WEB' | 'PAYMENT_PAGE_BUTTON_CLICKED_WEB' | 'BOTTOMSHEET_DISMISSED_WEB' | 'BOTTOMSHEET_CTA_CLICKED_WEB' | 'BOTTOMSHEET_TRIGGERED_WEB' | 'WEB_OTP_AUTO_READ' | 'WEB_OTP_FALLBACK_TRIGGERED';

// Interface for platform-specific event configuration
interface PlatformEventConfig {
    adjustToken?: string;         // 6-character event token for Adjust
    ga4Name?: string;             // Event name string for GA4 (gtag)
    moengageName?: string;        // Event name string for MoEngage
    metaPixelName?: string;       // Standard event name for Meta Pixel (fbq 'track')
    metaPixelCustomName?: string; // Custom event name for Meta Pixel (fbq 'trackCustom')
}

// Mapping of internal event types to platform-specific configs
// NOTE: You need to replace the placeholder tokens with actual Adjust Event Tokens from your dashboard
const EVENT_CONFIG: Record<AnalyticsEventType, PlatformEventConfig> = {
    PAYMENT_INITIATED: {
        adjustToken: process.env.NEXT_PUBLIC_ADJUST_WEB_CASHFREE_PAYMENT_INITIATED || '', // TODO: Replace with actual token
        ga4Name: 'web_payment_initiated',
        moengageName: 'web_payment_initiated'
    },
    DOWNLOAD_INITIATED: {
        adjustToken: process.env.NEXT_PUBLIC_ADJUST_WEB_CASHFREE_DOWNLOAD_INITIATED || '', // TODO: Replace with actual token
        ga4Name: 'web_download_initiated',
        moengageName: 'web_download_initiated',
        metaPixelName: 'InitiateCheckout'
    },
    WEB_SUBSCRIBE_BUTTON_CLICKED: {
        adjustToken: process.env.NEXT_PUBLIC_ADJUST_WEB_CASHFREE_SUBSCRIBE_BUTTON_CLICKED || '', // TODO: Replace with actual token
        ga4Name: 'web_subscribe_button_clicked',
        moengageName: 'web_subscribe_button_clicked',
        metaPixelName: 'AddToCart'
    },
    PAYMENT_ERROR: {
        adjustToken: '', // Add token if needed later
        ga4Name: 'web_payment_error',
        moengageName: 'web_payment_error'
    },
    LOGIN_PAGE_VISITED: {
        adjustToken: process.env.NEXT_PUBLIC_ADJUST_WEB_CASHFREE_LOGIN_PAGE_VISITED || '',
        ga4Name: 'web_login_page_visited',
        moengageName: 'web_login_page_visited',
        metaPixelName: 'AddToWishlist'
    },
    PAYMENT_CANCELLED: {
        adjustToken: '', // No token provided for Adjust yet
        ga4Name: 'web_payment_flow_cancelled_inbetween',
        moengageName: 'web_payment_flow_cancelled_inbetween'
    },
    USER_LEFT_BEFORE_SUBSCRIBING: {
        adjustToken: '',
        ga4Name: 'user_left_before_subscribing',
        moengageName: 'user_left_before_subscribing'
    },
    WEB_SUBSCRIBE_PAGE_VISITED: {
        adjustToken: process.env.NEXT_PUBLIC_ADJUST_WEB_CASHFREE_SUBSCRIBE_PAGE_VISITED || '',
        ga4Name: 'web_subscribe_page_visited',
        moengageName: 'web_subscribe_page_visited',
        metaPixelName: 'PageView_1'
    },
    WEB_DOWNLOAD_PAGE_VISITED: {
        adjustToken: process.env.NEXT_PUBLIC_ADJUST_WEB_CASHFREE_DOWNLOAD_PAGE_VISITED || '',
        ga4Name: 'web_download_page_visited',
        moengageName: 'web_download_page_visited',
        metaPixelName: 'Search'
    },
    WEB_BOTTOM_SHEET_VIEWED: {
        ga4Name: 'web_bottom_sheet_viewed',
        moengageName: 'web_bottom_sheet_viewed'
    },
    WEB_PAYMENT_METHOD_SELECTED: {
        ga4Name: 'web_payment_method_selected',
        moengageName: 'web_payment_method_selected'
    },
    WEB_BOTTOM_SHEET_BTN_CLICKED: {
        ga4Name: 'web_bottom_sheet_btn_clicked',
        moengageName: 'web_bottom_sheet_btn_clicked'
    },
    WEB_POLLING_CANCELLED: {
        ga4Name: 'web_polling_cancelled',
        moengageName: 'web_polling_cancelled'
    },
    WEB_PAYMENT_SUCCESS_SCREEN_VIEWED: {
        ga4Name: 'web_payment_success_screen_viewed',
        moengageName: 'web_payment_success_screen_viewed'
    },
    WEB_PAYMENT_FAILURE_SCREEN_VIEWED: {
        ga4Name: 'web_payment_failure_screen_viewed',
        moengageName: 'web_payment_failure_screen_viewed'
    },
    WEB_SEND_OTP_BTN_CLICKED: {
        ga4Name: 'web_send_otp_btn_clicked',
        moengageName: 'web_send_otp_btn_clicked'
    },
    WEB_VERIFY_OTP_BTN_CLICKED: {
        ga4Name: 'web_verify_otp_btn_clicked',
        moengageName: 'web_verify_otp_btn_clicked'
    },
    WEB_SOCIAL_LOGIN_BTN_CLICKED: {
        ga4Name: 'web_social_login_btn_clicked',
        moengageName: 'web_social_login_btn_clicked'
    },
    WEB_RESEND_OTP_BTN_CLICKED: {
        ga4Name: 'web_resend_otp_btn_clicked',
        moengageName: 'web_resend_otp_btn_clicked'
    },
    WEB_LOGIN_SUCCESS: {
        ga4Name: 'web_login_success',
        moengageName: 'web_login_success'
    },
    WEB_LOGIN_FAILED: {
        ga4Name: 'web_login_failed',
        moengageName: 'web_login_failed'
    },
    WEB_OTP_AUTO_READ: {
        ga4Name: 'web_otp_auto_read',
        moengageName: 'web_otp_auto_read'
    },
    WEB_OTP_FALLBACK_TRIGGERED: {
        ga4Name: 'web_otp_fallback_triggered',
        moengageName: 'web_otp_fallback_triggered'
    },
    REFERRAL_CODE_BTN_CLICKED: {
        ga4Name: 'referral_code_btn_clicked',
        moengageName: 'referral_code_btn_clicked'
    },
    APPLY_CODE_BTN_CLICKED: {
        ga4Name: 'apply_code_btn_clicked',
        moengageName: 'apply_code_btn_clicked'
    },
    CLAIM_CODE_BTN_CLICKED: {
        ga4Name: 'claim_code_btn_clicked',
        moengageName: 'claim_code_btn_clicked'
    },
    WEB_PAYMENT_SCREEN_MEDIA: {
        ga4Name: 'web_payment_screen_media',
        moengageName: 'web_payment_screen_media'
    },
    WEB_PAYMENT_SCREEN_VIDEO_PAUSE: {
        ga4Name: 'web_payment_screen_video_pause',
        moengageName: 'web_payment_screen_video_pause'
    },
    WEB_PAYMENT_SCREEN_VIDEO_PLAY: {
        ga4Name: 'web_payment_screen_video_play',
        moengageName: 'web_payment_screen_video_play'
    },
    WEB_PAYMENT_SCREEN_VIDEO_MUTED: {
        ga4Name: 'web_payment_screen_video_muted',
        moengageName: 'web_payment_screen_video_muted'
    },
    WEB_PAYMENT_SCREEN_VIDEO_UNMUTED: {
        ga4Name: 'web_payment_screen_video_unmuted',
        moengageName: 'web_payment_screen_video_unmuted'
    },
    CONTENT_IMPRESSION_WEB: {
        ga4Name: 'content_impression_web',
        moengageName: 'content_impression_web',
        metaPixelCustomName: 'content_impression_web'
    },
    CONTENT_CLICKED_WEB: {
        ga4Name: 'content_clicked_web',
        moengageName: 'content_clicked_web',
        metaPixelCustomName: 'content_clicked_web'
    },
    SCREEN_VIEWED_WEB: {
        ga4Name: 'Screen_viewed_web',
        moengageName: 'Screen_viewed_web',
        metaPixelCustomName: 'Screen_viewed_web'
    },
    FREE_TRIAL_NUDGE_VIEWED_WEB: {
        ga4Name: 'free_trial_nudge_viewed_web',
        moengageName: 'free_trial_nudge_viewed_web',
        metaPixelCustomName: 'free_trial_nudge_viewed_web'
    },
    FREE_TRIAL_NUDGE_CTA_CLICKED_WEB: {
        ga4Name: 'free_trial_nudge_cta_clicked_web',
        moengageName: 'free_trial_nudge_cta_clicked_web',
        metaPixelCustomName: 'free_trial_nudge_cta_clicked_web'
    },
    SEARCH_BUTTON_CLICKED_WEB: {
        ga4Name: 'Search_button_clicked_web',
        moengageName: 'Search_button_clicked_web',
        metaPixelCustomName: 'Search_button_clicked_web'
    },
    PROFILE_BUTTON_CLICKED_WEB: {
        ga4Name: 'Profile_button_clicked_web',
        moengageName: 'Profile_button_clicked_web',
        metaPixelCustomName: 'Profile_button_clicked_web'
    },
    MYLIST_BUTTON_CLICKED_WEB: {
        ga4Name: 'Mylist_button_clicked_web',
        moengageName: 'Mylist_button_clicked_web',
        metaPixelCustomName: 'Mylist_button_clicked_web'
    },
    HOME_BUTTON_CLICKED_WEB: {
        ga4Name: 'Home_button_clicked_web',
        moengageName: 'Home_button_clicked_web',
        metaPixelCustomName: 'Home_button_clicked_web'
    },
    PAYMENT_PAGE_BUTTON_CLICKED_WEB: {
        ga4Name: 'Payment_page_button_clicked_web',
        moengageName: 'Payment_page_button_clicked_web',
        metaPixelCustomName: 'Payment_page_button_clicked_web'
    },
    BOTTOMSHEET_DISMISSED_WEB: {
        ga4Name: 'Bottomsheet_dismissed_web',
        moengageName: 'Bottomsheet_dismissed_web',
        metaPixelCustomName: 'Bottomsheet_dismissed_web'
    },
    BOTTOMSHEET_CTA_CLICKED_WEB: {
        ga4Name: 'Bottomsheet_cta_clicked_web',
        moengageName: 'Bottomsheet_cta_clicked_web',
        metaPixelCustomName: 'Bottomsheet_cta_clicked_web'
    },
    BOTTOMSHEET_TRIGGERED_WEB: {
        ga4Name: 'Bottomsheet_triggered_web',
        moengageName: 'Bottomsheet_triggered_web',
        metaPixelCustomName: 'Bottomsheet_triggered_web'
    }
};

/**
 * Common function to track events across all configured platforms.
 * @param eventType The type of event to track (defined in AnalyticsEventType)
 * @param params Optional parameters to pass to the event
 */
export const trackAnalyticsEvent = (eventType: AnalyticsEventType, params?: AnalyticsEventParams) => {
    const config = EVENT_CONFIG[eventType];

    if (!config) {
        console.warn(`No configuration found for analytics event: ${eventType}`);
        return;
    }

    // 1. Track MoEngage
    if (config.moengageName) {
        trackMoEngageEvent(config.moengageName, params);
    }

    // 2. Track Adjust
    // Adjust params are strictly Record<string, string>, so we might need to stringify values if they are complex
    if (config.adjustToken) {
        const adjustParams: Record<string, string> = {};
        if (params) {
            Object.entries(params).forEach(([key, value]) => {
                if (typeof value === 'object') {
                    adjustParams[key] = JSON.stringify(value);
                } else {
                    adjustParams[key] = String(value);
                }
            });
        }
        trackAdjustEvent(config.adjustToken, adjustParams);
    }

    // 3. Track Meta Pixel (standard + custom events)
    const fbq = typeof window !== 'undefined' ? (window as Window & { fbq?: Fbq }).fbq : undefined;
    if (typeof fbq === 'function') {
        if (config.metaPixelName) {
            fbq('track', config.metaPixelName, params || {});
        } else if (config.metaPixelCustomName) {
            fbq('trackCustom', config.metaPixelCustomName, params || {});
        }
    }

    // 4. Track GA4
    if (config.ga4Name) {
        trackGa4Event(config.ga4Name, params);
    }
};
